import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import TeamAccountEmployeeService from '../services/TeamAccountEmployeeService';
import { User, UserPagination, EmployeesFilter } from '../../types';

// Cache keys
export const CACHE_KEY_TEAM_ACCOUNT_EMPLOYEES = 'teamAccountEmployees';
export const CACHE_KEY_EMPLOYEE_TEAM_ACCOUNTS = 'employeeTeamAccounts';

interface TeamAccountEmployeesResponse {
    data: User[];
}

interface AssignEmployeesResponse {
    message: string;
}

/**
 * Get assigned employees for a team account (non-paginated)
 */
export const useGetTeamAccountAssignedEmployees = (teamAccountId: number, enabled: boolean = true) => {
    return useQuery<TeamAccountEmployeesResponse, AxiosError>(
        [CACHE_KEY_TEAM_ACCOUNT_EMPLOYEES, teamAccountId],
        () => TeamAccountEmployeeService.getTeamAccountAssignedEmployees(teamAccountId),
        {
            enabled: enabled && !!teamAccountId,
            staleTime: 2 * 60 * 1000, // 2 minutes
            cacheTime: 5 * 60 * 1000, // 5 minutes
        }
    );
};

/**
 * Get assigned employees for a team account with pagination (infinite query)
 */
export const useGetTeamAccountAssignedEmployeesInfinite = (teamAccountId: number, filter?: EmployeesFilter, enabled: boolean = true) => {
    return useInfiniteQuery<UserPagination, AxiosError>(
        [CACHE_KEY_TEAM_ACCOUNT_EMPLOYEES, teamAccountId, 'infinite', filter],
        ({ pageParam = 1 }) =>
            TeamAccountEmployeeService.getTeamAccountAssignedEmployeesPaginated(teamAccountId, {
                ...filter,
                page: pageParam,
                perPage: filter?.perPage || 20,
            }),
        {
            enabled: enabled && !!teamAccountId,
            getNextPageParam: (lastPage) => {
                if (lastPage.meta.current_page < lastPage.meta.last_page) {
                    return lastPage.meta.current_page + 1;
                }
                return undefined;
            },
            staleTime: 2 * 60 * 1000,
            cacheTime: 5 * 60 * 1000,
        }
    );
};

/**
 * Assign employees to a team account (replaces existing assignments)
 */
export const useAssignEmployeesToTeamAccount = (
    onSuccess?: (data: AssignEmployeesResponse, variables: { teamAccountId: number; employeeIds: number[] }) => void,
    onError?: (error: AxiosError) => void
) => {
    const queryClient = useQueryClient();

    return useMutation<AssignEmployeesResponse, AxiosError, { teamAccountId: number; employeeIds: number[] }>(
        ({ teamAccountId, employeeIds }) => TeamAccountEmployeeService.assignEmployeesToTeamAccount(teamAccountId, employeeIds),
        {
            onSuccess: (data, variables) => {
                // Invalidate team account employees list
                queryClient.invalidateQueries([CACHE_KEY_TEAM_ACCOUNT_EMPLOYEES, variables.teamAccountId]);

                // Invalidate all employee lists (filtering might be affected)
                queryClient.invalidateQueries(['employees']);

                onSuccess?.(data, variables);
            },
            onError: (error) => {
                onError?.(error);
            },
        }
    );
};

/**
 * Add employees to a team account (append to existing)
 */
export const useAddEmployeesToTeamAccount = (
    onSuccess?: (data: AssignEmployeesResponse, variables: { teamAccountId: number; employeeIds: number[] }) => void,
    onError?: (error: AxiosError) => void
) => {
    const queryClient = useQueryClient();

    return useMutation<AssignEmployeesResponse, AxiosError, { teamAccountId: number; employeeIds: number[] }>(
        ({ teamAccountId, employeeIds }) => TeamAccountEmployeeService.addEmployeesToTeamAccount(teamAccountId, employeeIds),
        {
            onSuccess: (data, variables) => {
                queryClient.invalidateQueries([CACHE_KEY_TEAM_ACCOUNT_EMPLOYEES, variables.teamAccountId]);
                queryClient.invalidateQueries(['employees']);
                onSuccess?.(data, variables);
            },
            onError: (error) => {
                onError?.(error);
            },
        }
    );
};

/**
 * Remove employees from a team account
 */
export const useRemoveEmployeesFromTeamAccount = (
    onSuccess?: (data: AssignEmployeesResponse, variables: { teamAccountId: number; employeeIds: number[] }) => void,
    onError?: (error: AxiosError) => void
) => {
    const queryClient = useQueryClient();

    return useMutation<AssignEmployeesResponse, AxiosError, { teamAccountId: number; employeeIds: number[] }>(
        ({ teamAccountId, employeeIds }) => TeamAccountEmployeeService.removeEmployeesFromTeamAccount(teamAccountId, employeeIds),
        {
            onSuccess: (data, variables) => {
                queryClient.invalidateQueries([CACHE_KEY_TEAM_ACCOUNT_EMPLOYEES, variables.teamAccountId]);
                queryClient.invalidateQueries(['employees']);
                onSuccess?.(data, variables);
            },
            onError: (error) => {
                onError?.(error);
            },
        }
    );
};

/**
 * Get team accounts assigned to an employee
 */
export const useGetEmployeeAssignedTeamAccounts = (employeeId: number, enabled: boolean = true) => {
    return useQuery<TeamAccountEmployeesResponse, AxiosError>(
        [CACHE_KEY_EMPLOYEE_TEAM_ACCOUNTS, employeeId],
        () => TeamAccountEmployeeService.getEmployeeAssignedTeamAccounts(employeeId),
        {
            enabled: enabled && !!employeeId,
            staleTime: 2 * 60 * 1000, // 2 minutes
            cacheTime: 5 * 60 * 1000, // 5 minutes
        }
    );
};
